import React from 'react';
import { useScrollFade } from '../../hooks/useScrollFade';

/**
 * TableCard — the table container (spec §3.5): a card (28px radius, hairline, card shadow)
 * around a horizontally scrolling table, with a faded right edge while columns are clipped and
 * an optional footer for <Pagination>. The table itself is `<table className="ui-table">`
 * (heads and rows are styled globally; see styles/ui.css for the cell helpers).
 *
 *   <TableCard aria-label="O'quvchilar" footer={<Pagination state={pagination} />}>
 *     <table className="ui-table"> … </table>
 *   </TableCard>
 *
 * Props: aria-label (names the scroll region and makes it keyboard-scrollable), footer, tall (more
 * than ~12 rows: caps the height at min(70dvh, 720px) so the sticky head works), className.
 * Phones: give the table `ui-table--stack` and the secondary cells `data-label` to stack rows
 * instead of scrolling sideways (the head is hidden then: a sortable table also needs a phone
 * control for its order, e.g. a <Dropdown> of the sortable columns).
 * Numeric columns: `className="ui-num"` on the th and the cells right-aligns them; sortable heads
 * are <SortableTh> (a real button, aria-sort, an arrow).
 */
function TableCard({ children, footer, tall = false, className = '', 'aria-label': ariaLabel }) {
  const { ref, fade } = useScrollFade();
  const scrollProps = ariaLabel ? { role: 'region', 'aria-label': ariaLabel, tabIndex: 0 } : {};
  return (
    <div className={`ui-table-card ${className}`.trim()}>
      <div
        ref={ref}
        className={`ui-table-scroll${tall ? ' ui-table-scroll--tall' : ''} ${fade}`.trim()}
        {...scrollProps}
      >
        {children}
      </div>
      {footer && <div className="ui-table-footer">{footer}</div>}
    </div>
  );
}

export default TableCard;
